<template>
  <el-menu-item :style="{ marginBottom: mini ? 'inherited' : '20px' }">
    <font-awesome-icon
      v-if="mini"
      color="#409EFF"
      icon="sticky-note"
      size="lg"
      @click="showDialog"
    />
    <template #title>
      <el-button
        class="add-note__button"
        type="primary"
        :link="true"
        size="small"
        @click="showDialog"
      >
        <font-awesome-icon
          class="add-note__icon"
          icon="sticky-note"
          size="lg"
        />
        Add note
      </el-button>
    </template>

    <el-drawer
      ref="drawer"
      v-model="dialog"
      title="User Notes"
      direction="ltr"
      :append-to-body="true"
      custom-class="add-note__drawer"
      :before-close="cancelForm"
      :size="450"
    >
      <div class="add-note__flex-container">
        <div class="add-note__form">
          <el-form
            :model="form"
            :label-width="formLabelWidth"
            style="padding-right: 20px"
          >
            <el-form-item label="Note">
              <el-input
                v-model="form.text"
                :rows="15"
                :autosize="{ minRows: 5, maxRows: 20 }"
                type="textarea"
                autocomplete="off"
              />
            </el-form-item>
            <el-form-item label="Notify at">
              <el-date-picker
                v-model="form.notify_at"
                type="date"
                placeholder="Select date and time"
              />
            </el-form-item>
          </el-form>
        </div>

        <div class="add-note__content">
          <el-card
            v-for="note in userNotes"
            :key="note.id"
            shadow="hover"
            style="margin-bottom: 2px"
          >
            <el-row type="flex" class="row-bg" justify="space-between">
              <el-col :span="8">
                <div class="grid-content">
                  <b>
                    {{ note.attributes.author }}
                  </b>
                </div>
              </el-col>
              <el-col v-if="!!note.attributes.notify_at" :span="6">
                <div class="grid-content">
                  <font-awesome-icon icon="bell" size="sm" />
                  {{ new Date(note.attributes.notify_at).toLocaleDateString() }}
                </div>
              </el-col>
              <el-col :span="10">
                <div class="grid-content" style="text-align: right">
                  {{ formatDate(note.attributes.created_at) }}
                </div>
              </el-col>
            </el-row>
            <el-row type="flex" justify="center" style="margin-top: 10px">
              <el-col>
                <div class="grid-content">
                  {{ note.attributes.text }}
                </div>
              </el-col>
            </el-row>
          </el-card>
        </div>
        <div class="add-note__footer">
          <el-button @click="cancelForm"> Cancel </el-button>
          <el-button
            type="primary"
            :loading="loading"
            :disabled="isTextEmpty"
            @click="handleSubmit"
          >
            {{ loading ? 'Submitting ...' : 'Submit' }}
          </el-button>
        </div>
      </div>
    </el-drawer>
  </el-menu-item>
</template>

<script lang="ts">
import { ref, defineComponent, onMounted, computed, Ref } from 'vue'
import { useUserNotesStore } from '@/store/modules/userNotes'
import { IUserNote, IUserNotesPayload } from '@/interfaces/userNotes'
import { ElMessage } from 'element-plus'

import { safeSubmitProcessing, scrollToFirstError } from '@/utils/helpers'
import useValidation from '@/hooks/useValidation'
import { CommonValidationErrorsKey } from '@/api/types'
export default {
  name: 'AddNote',
  props: {
    mini: {
      type: Boolean,
      default: false
    }
  },
  setup(props: any, context: any) {
    const userNotesStore = useUserNotesStore()
    userNotesStore.FetchItems()

    const dialog = ref(false)
    const loading = ref(false)
    const { provideValidation, setValidationErrors } = useValidation(
      CommonValidationErrorsKey
    )
    const errors = provideValidation()

    const form = ref<IUserNotesPayload>({
      text: '',
      notify_at: null
    })

    const userNotes = computed<Array<IUserNote>>(() => {
      return userNotesStore.currentItems || []
    })

    const isTextEmpty = computed<boolean>(() => {
      return form.value.text === ''
    })

    const handleSubmit = async () => {
      if (loading.value) {
        return
      }
      if (window.confirm('Do you want to submit?')) {
        await safeSubmitProcessing(
          loading,
          () => userNotesStore.SaveItem({ ...form.value }),
          () => {
            form.value = { text: '', notify_at: null }
            ElMessage.success('UserNote has been successfully created.')
            userNotesStore.FetchItems()
            errors.value = {}
          },
          (err) => {
            ElMessage.error('smth went wrong')
            setValidationErrors(err, errors)
          }
        )
      }
    }
    const showDialog = () => {
      dialog.value = true
    }
    const cancelForm = () => {
      errors.value = {}
      loading.value = false
      dialog.value = false
    }

    const formatDate = (value: string | number) => {
      return new Date(value).toLocaleString()
    }

    return {
      dialog,
      form,
      loading,
      userNotes,
      handleSubmit,
      cancelForm,
      formatDate,
      isTextEmpty,
      showDialog
    }
  },
  data() {
    return {
      formLabelWidth: '80px'
    }
  }
}
</script>

<style scoped>
.add-note__flex-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.add-note__button {
  width: 100%;
  margin-bottom: 7px;
}

.add-note__icon {
  margin-right: 4px;
}

.add-note__form {
  flex: 0 0 auto;
  border-bottom: 1px solid grey;
  box-shadow: 0 10px 10px white;
}

.add-note__content {
  flex: 1 1 auto;
  overflow-y: auto;
}

.add-note__footer {
  flex: 0 0 auto;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 20px;
  text-align: right;
  background-color: white;
  box-shadow: 0 -10px 10px white;
}
</style>
